import React, { useState } from "react";
import { useParams } from "react-router";
import { forgotPassword } from "../../utils/Auth";
import CssBaseline from "@mui/material/CssBaseline";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Container from "@mui/material/Container";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import { ThemeProvider, useTheme } from "@mui/material/styles";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import Visibility from "@mui/icons-material/Visibility";
import { FormHelperText } from "@mui/material";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { FormControl, InputLabel, OutlinedInput } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { setSnackbar } from "../../actions/snackbarActions";
import { logOut } from "../../actions/authActions";
import LockResetIcon from "@mui/icons-material/LockReset";
import {
  getPasswordValidationErrors,
  isPasswordFormatValid,
} from "../../utils/passwordValidation";
import { Typography } from "@mui/material";
import PasswordChanged from "../../image/PasswordChanged.svg";
import Tooltip from "@mui/material/Tooltip";

/**
 * After clicking a link in the mail for password recovery this page is prompt.
 * Here user can enter new password and by doing updates his old password that he forgot.
 */
function ForgotPassword() {
  const { id, token } = useParams();
  const [password, setPassword] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [resetDone, setResetDone] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [pwdError, setPwdError] = useState(false);
  const [confirmPwdError, setConfirmPwdError] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState([]);
  const [confirmPasswordErrors, setConfirmPasswordErrors] = useState([]);

  const [values, setValues] = useState({
    showPassword: false,
    showPassword1: false,
  });

  const validatePasswordValue = (value) => {
    if (!value) {
      setPwdError(false);
      setPasswordErrors([]);
      return [];
    }
    const errors = getPasswordValidationErrors(value, t);
    setPwdError(errors.length > 0);
    setPasswordErrors(errors);
    return errors;
  };

  const validateConfirmPasswordValue = (pwd, confirmPassword) => {
    if (!confirmPassword) {
      setConfirmPwdError(false);
      setConfirmPasswordErrors([]);
      return [];
    }

    const errors = [...getPasswordValidationErrors(confirmPassword, t)];
    if (pwd !== confirmPassword) {
      errors.push(t("errorHelperText.passwordMatch"));
    }

    const unique = [...new Set(errors)];
    setConfirmPwdError(unique.length > 0);
    setConfirmPasswordErrors(unique);
    return unique;
  };

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setPassword(value);
    validatePasswordValue(value);
    if (confirmPw) {
      validateConfirmPasswordValue(value, confirmPw);
    }
  };

  const handleConfirmPasswordChange = (e) => {
    const value = e.target.value;
    setConfirmPw(value);
    validateConfirmPasswordValue(password, value);
  };

  const onSubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (isSubmitting) return;

    const pwd = password.trim();
    const confirm = confirmPw.trim();
    // Both empty, or only one filled
    if (!pwd || !confirm) {
      const mandatory = t("snackbar.mandatoryFields");
      if (!pwd) {
        setPwdError(true);
        setPasswordErrors([mandatory]);
      }
      if (!confirm) {
        setConfirmPwdError(true);
        setConfirmPasswordErrors([mandatory]);
      }
      dispatch(setSnackbar(true, "error", mandatory));
      return;
    }

    // Passwords do not match — do not call BE
    if (password !== confirmPw) {
      const matchError = t("errorHelperText.passwordMatch");
      setConfirmPwdError(true);
      setConfirmPasswordErrors([matchError]);
      dispatch(setSnackbar(true, "error", matchError));
      return;
    }

    const pwdErrors = validatePasswordValue(password);
    const confirmErrors = validateConfirmPasswordValue(password, confirmPw);

    if (
      pwdErrors.length ||
      confirmErrors.length ||
      !isPasswordFormatValid(password)
    ) {
      dispatch(
        setSnackbar(
          true,
          "error",
          pwdErrors[0] || confirmErrors[0] || t("snackbar.passwordChangedError"),
        ),
      );
      return;
    }

    setIsSubmitting(true);

    forgotPassword(id, token, password)
      .then((response) => {
        const status = Number(response?.statusCode ?? response?.status);
        if (status >= 200 && status < 300) {
          localStorage.removeItem("userName");
          dispatch(logOut());
          navigate("/", {
            replace: true,
            state: { passwordResetSuccess: true },
          });
          return;
        }

        setIsSubmitting(false);
        dispatch(
          setSnackbar(
            true,
            "error",
            response?.message || t("snackbar.passwordChangedError"),
          ),
        );
      })
      .catch(() => {
        setIsSubmitting(false);
        dispatch(
          setSnackbar(true, "error", t("snackbar.passwordChangedError")),
        );
      });
  };

  const handleClickShowPassword = () => {
    setValues({
      ...values,
      showPassword: !values.showPassword,
    });
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleClickShowPassword1 = () => {
    setValues({
      ...values,
      showPassword1: !values.showPassword1,
    });
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Container
        className="con"
        component="main"
        maxWidth="md"
        sx={{
          mb: 4,
          height: "calc(72vh - 50px)",
          width: { xs: 200, sm: 400, md: 400, lg: 500, xl: 600 },
        }}
      >
        <Paper
          variant="outlined"
          sx={{ my: { xs: 3, md: 6 }, p: { xs: 2, md: 3 }, align: "center" }}
        >
          {!resetDone ? (
            <Box
              component="form"
              noValidate
              sx={{ mt: 1 }}

            >
              <Grid
                container
                spacing={0}
                direction="column"
                alignItems="center"
                justifyContent="center"
                style={{ minHeight: "10vh" }}
              >
                <Grid item xs={3}>
                  <LockResetIcon
                    m="auto"
                    sx={{ fontSize: 75, color: theme.palette.secondary.main }}
                  />
                </Grid>
              </Grid>
              <Tooltip
                title={t("registration.passwordTooltip")}
                placement="top-start"
              >
                <FormControl fullWidth required sx={{ mt: 2 }} error={pwdError}>
                  <InputLabel htmlFor="forgot-password">
                    {t("resetpassword.pas")}
                  </InputLabel>
                  <OutlinedInput
                    id="forgot-password"
                    name="password"
                    label={t("resetpassword.pas")}
                    type={values.showPassword ? "text" : "password"}
                    autoComplete="new-password"
                    value={password}
                    onChange={handlePasswordChange}
                    onBlur={(e) => validatePasswordValue(e.target.value)}
                    error={pwdError}
                    endAdornment={
                      <InputAdornment
                        position="end"
                        sx={{ right: 20, position: "absolute" }}
                      >
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowPassword}
                          onMouseDown={handleMouseDownPassword}
                          edge="end"
                          tabIndex={-1}
                        >
                          {values.showPassword ? (
                            <VisibilityOff />
                          ) : (
                            <Visibility />
                          )}
                        </IconButton>
                      </InputAdornment>
                    }
                  />
                  {passwordErrors.map((errorText, index) => (
                    <FormHelperText error key={`pwd-err-${index}`}>
                      {errorText}
                    </FormHelperText>
                  ))}
                </FormControl>
              </Tooltip>
              <FormControl
                fullWidth
                required
                sx={{ mt: 2 }}
                error={confirmPwdError}
              >
                <InputLabel htmlFor="forgot-confirm-password">
                  {t("resetpassword.confirmpas")}
                </InputLabel>
                <OutlinedInput
                  id="forgot-confirm-password"
                  name="confirmPassword"
                  label={t("resetpassword.confirmpas")}
                  type={values.showPassword1 ? "text" : "password"}
                  autoComplete="new-password"
                  value={confirmPw}
                  onChange={handleConfirmPasswordChange}
                  onBlur={() =>
                    validateConfirmPasswordValue(password, confirmPw)
                  }
                  error={confirmPwdError}
                  endAdornment={
                    <InputAdornment
                      position="end"
                      sx={{ right: 20, position: "absolute" }}
                    >
                      <IconButton
                        aria-label="toggle confirm password visibility"
                        onClick={handleClickShowPassword1}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                        tabIndex={-1}
                      >
                        {values.showPassword1 ? (
                          <VisibilityOff />
                        ) : (
                          <Visibility />
                        )}
                      </IconButton>
                    </InputAdornment>
                  }
                />
                {confirmPasswordErrors.map((errorText, index) => (
                  <FormHelperText error key={`confirm-pwd-err-${index}`}>
                    {errorText}
                  </FormHelperText>
                ))}
              </FormControl>
              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={isSubmitting}
                sx={{
                  mt: 3,
                  mb: 2,
                  background: theme.palette.secondary.main,
                  fontSize: 20,
                  "&.Mui-disabled": {
                    background: theme.palette.action.disabledBackground,
                    color: theme.palette.action.disabled,
                  },
                }}
                onClick={onSubmit}
              >
                {t("resetpassword.pasbtn")}
              </Button>
            </Box>
          ) : (
            <Grid
              container
              spacing={0}
              direction="column"
              alignItems="center"
              justifyContent="center"
              py={2}
              sx={{ minHeight: "50vh" }}
            >
              <Grid item xs={12}>
                <Typography variant="h4">
                  Success! Go back to login page.
                </Typography>
              </Grid>
              <Grid item xs={8}>
                <Box>
                  <img src={PasswordChanged} alt="Password changed" />
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Button
                  onClick={() => navigate("/")}
                  size="large"
                  variant="contained"
                  sx={{ backgroundColor: theme.palette.secondary.main }}
                >
                  {t("resetpassword.click")} {t("resetpassword.here")}
                </Button>
              </Grid>
            </Grid>
          )}
        </Paper>
      </Container>
    </ThemeProvider>
  );
}

export default ForgotPassword;
